import { LayaGL } from "../../../layagl/LayaGL";
import { Matrix4x4 } from "../../../maths/Matrix4x4";
import { OutOfRangeError } from "../../../utils/Error";
import { Stat } from "../../../utils/Stat";
import { Laya3DRender } from "../../RenderObjs/Laya3DRender";
import { UnlitMaterial } from "../material/UnlitMaterial";
import { MeshSprite3DShaderDeclaration } from "../MeshSprite3DShaderDeclaration";
import { BaseRender } from "../render/BaseRender";
import { RenderElement } from "../render/RenderElement";
import { PixelLineData } from "./PixelLineData";
import { PixelLineFilter } from "./PixelLineFilter";
import { PixelLineMaterial } from "./PixelLineMaterial";
export class PixelLineRenderer extends BaseRender {
    constructor() {
        super();
        this._isRenderActive = false;
        this._isInRenders = false;
        this._needUpdatelines = false;
        this._lines = [];
        this._projectionViewWorldMatrix = new Matrix4x4();
        this._pixelLineFilter = new PixelLineFilter(this, 20);
        this._baseRenderNode.shaderData.addDefine(MeshSprite3DShaderDeclaration.SHADERDEFINE_COLOR);
        this.geometryBounds = this._pixelLineFilter._bounds;
    }
    get bounds() {
        var lineFilter = this._pixelLineFilter;
        lineFilter._reCalculateBound();
        return super.bounds;
    }
    get pixelLinesDatas() {
        if (this._needUpdatelines) {
            this._updateLineDatas();
        }
        return this._lines;
    }
    set pixelLinesDatas(value) {
        this.clear();
        if (value.length > this._pixelLineFilter._maxLineCount) {
            value = value.slice(0, this._pixelLineFilter._maxLineCount);
            console.warn("reach max line count");
        }
        this.addLines(value);
    }
    get maxLineCount() {
        return this._pixelLineFilter._maxLineCount;
    }
    set maxLineCount(value) {
        this._pixelLineFilter._resizeLineData(value);
        this._pixelLineFilter._lineCount = Math.min(this._pixelLineFilter._lineCount, value);
    }
    get lineCount() {
        return this._pixelLineFilter._lineCount;
    }
    _onAdded() {
        super._onAdded();
        this._changeRenderObjects(0, PixelLineMaterial.defaultMaterial);
    }
    _onEnable() {
        super._onEnable();
        this._isRenderActive = true;
        if (this._pixelLineFilter._lineCount != 0) {
            (this.owner.scene)._addRenderObject(this);
            this._isInRenders = true;
        }
        this._setBelongScene(this.owner.scene);
    }
    _onDisable() {
        if (this._pixelLineFilter && this._pixelLineFilter._lineCount != 0 && this._isRenderActive) {
            this.owner.scene._removeRenderObject(this);
            this._isInRenders = false;
        }
        this._isRenderActive = false;
        this._setUnBelongScene();
    }
    _createBaseRenderNode() {
        return Laya3DRender.Render3DModuleDataFactory.createMeshRenderNode();
    }
    _onWorldMatNeedChange(flag) {
        super._onWorldMatNeedChange(flag);
        this._baseRenderNode.ismoved.setValue(Stat.loopCount, LayaGL.renderEngine._framePassCount);
        this._baseRenderNode.ismoved = this._baseRenderNode.ismoved;
    }
    renderUpdate(context) {
        this._renderElements.forEach((element, index) => {
            var _a, _b;
            element._renderElementOBJ.isRender = element._geometry._prepareRender(context);
            element._geometry._updateRenderParams(context);
            let material = (_a = this.sharedMaterial) !== null && _a !== void 0 ? _a : UnlitMaterial.defaultMaterial;
            material = (_b = this.sharedMaterials[index]) !== null && _b !== void 0 ? _b : material;
            element.material = material;
            element._renderElementOBJ.materialRenderQueue = material.renderQueue;
        });
    }
    _changeRenderObjects(index, material) {
        var renderObjects = this._renderElements;
        (material) || (material = PixelLineMaterial.defaultMaterial);
        var renderElement = renderObjects[index];
        (renderElement) || (renderElement = renderObjects[index] = new RenderElement());
        renderElement.setTransform(this.owner._transform);
        renderElement.setGeometry(this._pixelLineFilter);
        renderElement.render = this;
        renderElement.material = material;
        this._setRenderElements();
    }
    _pixelLinesDataChange(key) {
        if (key != null) {
            let keyN = parseInt(key);
            let line = this._lines[keyN];
            if (line) {
                this.setLine(keyN, line.startPosition, line.endPosition, line.startColor, line.endColor);
            }
        }
    }
    addLine(startPosition, endPosition, startColor, endColor) {
        if (this._pixelLineFilter._lineCount !== this._pixelLineFilter._maxLineCount) {
            this._pixelLineFilter._updateLineData(this._pixelLineFilter._lineCount++, startPosition, endPosition, startColor, endColor);
        }
        else {
            throw new Error("reach max line count");
        }
        if (this._isRenderActive && !this._isInRenders && this._pixelLineFilter._lineCount > 0) {
            this.owner.scene && this.owner.scene._addRenderObject(this);
            this._isInRenders = true;
        }
        this._needUpdatelines = true;
    }
    addLineWithNormal(startPosition, endPosition, startColor, endColor, startNormal, endNormal) {
        if (this._pixelLineFilter._lineCount !== this._pixelLineFilter._maxLineCount) {
            this._pixelLineFilter._updateLineData(this._pixelLineFilter._lineCount++, startPosition, endPosition, startColor, endColor, startNormal, endNormal);
        }
        else {
            throw new Error("reach max line count");
        }
        if (this._isRenderActive && !this._isInRenders && this._pixelLineFilter._lineCount > 0) {
            this.owner.scene && this.owner.scene._addRenderObject(this);
            this._isInRenders = true;
        }
        this._needUpdatelines = true;
    }
    addLines(lines) {
        var lineCount = this._pixelLineFilter._lineCount;
        var addCount = lines.length;
        if (lineCount + addCount > this._pixelLineFilter._maxLineCount) {
            throw new Error("reach max line count");
        }
        else {
            this._pixelLineFilter._updateLineDatas(lineCount, lines);
            this._pixelLineFilter._lineCount += addCount;
            this.boundsChange = true;
        }
        if (this._isRenderActive && !this._isInRenders && this._pixelLineFilter._lineCount > 0) {
            this.owner.scene && this.owner.scene._addRenderObject(this);
            this._isInRenders = true;
        }
        this._needUpdatelines = true;
    }
    removeLine(index) {
        if (index < this._pixelLineFilter._lineCount)
            this._pixelLineFilter._removeLineData(index);
        else
            throw new OutOfRangeError(index);
        if (this._isRenderActive && this._isInRenders && this._pixelLineFilter._lineCount == 0) {
            this.owner.scene && this.owner.scene._removeRenderObject(this);
            this._isInRenders = false;
        }
        this._needUpdatelines = true;
    }
    setLine(index, startPosition, endPosition, startColor, endColor) {
        if (index < this._pixelLineFilter._lineCount) {
            this._pixelLineFilter._updateLineData(index, startPosition, endPosition, startColor, endColor);
            let pixeldata = this._lines[index];
            if (pixeldata) {
                startColor.cloneTo(pixeldata.startColor);
                endColor.cloneTo(pixeldata.endColor);
                startPosition.cloneTo(pixeldata.startPosition);
                endPosition.cloneTo(pixeldata.endPosition);
            }
        }
        else
            throw new OutOfRangeError(index);
    }
    setLineWithNormal(index, startPosition, endPosition, startColor, endColor, startNormal, endNormal) {
        if (index < this._pixelLineFilter._lineCount) {
            this._pixelLineFilter._updateLineData(index, startPosition, endPosition, startColor, endColor, startNormal, endNormal);
            let pixeldata = this._lines[index];
            if (pixeldata) {
                startColor.cloneTo(pixeldata.startColor);
                endColor.cloneTo(pixeldata.endColor);
                startPosition.cloneTo(pixeldata.startPosition);
                endPosition.cloneTo(pixeldata.endPosition);
                startNormal && startNormal.cloneTo(pixeldata.startNormal);
                endNormal && endNormal.cloneTo(pixeldata.endNormal);
            }
        }
        else
            throw new OutOfRangeError(index);
    }
    getLine(index, out) {
        if (index < this.lineCount)
            this._pixelLineFilter._getLineData(index, out);
        else
            throw new OutOfRangeError(index);
    }
    _updateLineDatas() {
        let n = this.lineCount;
        this._lines = [];
        for (let i = 0; i < n; i++) {
            let pixelLineDatas = new PixelLineData();
            this.getLine(i, pixelLineDatas);
            this._lines.push(pixelLineDatas);
        }
        this._needUpdatelines = false;
    }
    clear() {
        this._pixelLineFilter._lineCount = 0;
        if (this._isRenderActive && this._isInRenders) {
            this.owner.scene && this.owner.scene._removeRenderObject(this);
            this._isInRenders = false;
        }
    }
    _onDestroy() {
        this._pixelLineFilter.destroy();
        this._pixelLineFilter = null;
        super._onDestroy();
    }
    _cloneTo(dest) {
        super._cloneTo(dest);
        dest.maxLineCount = this.maxLineCount;
        const lineCount = this.lineCount;
        let linedata = new PixelLineData();
        for (let i = 0, n = lineCount; i < n; i++) {
            this.getLine(i, linedata);
            dest.addLine(linedata.startPosition, linedata.endPosition, linedata.startColor, linedata.endColor);
        }
    }
}
